import React from 'react';
import { InboxIcon, FileText, Receipt, Users, Building2, BarChart3, Search, Upload, Plus } from 'lucide-react';

const ICON_MAP = {
  invoices: FileText,
  expenses: Receipt,
  users: Users,
  vendors: Building2,
  reports: BarChart3,
  search: Search,
  upload: Upload,
  default: InboxIcon,
};

/**
 * EmptyState — replaces blank lists with instructional content and a call to action.
 *
 * Props:
 *  icon       – lucide icon component, or a key from ICON_MAP
 *  title      – main heading text
 *  message    – supporting description
 *  action     – { label, onClick, href, icon } primary CTA
 *  secondaryAction – { label, onClick, href } secondary CTA
 *  compact    – boolean, smaller variant for inline use
 */
export default function EmptyState({
  icon,
  title = 'Nothing here yet',
  message,
  action,
  secondaryAction,
  compact = false,
}) {
  const IconComponent =
    typeof icon === 'string'
      ? (ICON_MAP[icon] || ICON_MAP.default)
      : (icon || ICON_MAP.default);

  const ActionIcon = action?.icon || Plus;

  if (compact) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mb-3">
          <IconComponent className="w-5 h-5 text-gray-400" />
        </div>
        <p className="text-sm font-medium text-gray-600">{title}</p>
        {message && <p className="text-xs text-gray-400 mt-1 max-w-xs">{message}</p>}
        {action && (
          <button
            onClick={action.onClick}
            className="mt-3 btn-primary btn-sm"
          >
            {action.label}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center mb-5 shadow-sm">
        <IconComponent className="w-8 h-8 text-blue-500" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      {message && (
        <p className="text-sm text-gray-500 max-w-sm leading-relaxed mb-6">{message}</p>
      )}
      {(action || secondaryAction) && (
        <div className="flex items-center gap-3">
          {action && (
            action.href ? (
              <a href={action.href} className="btn-primary flex items-center gap-2">
                <ActionIcon className="w-4 h-4" />
                {action.label}
              </a>
            ) : (
              <button onClick={action.onClick} className="btn-primary flex items-center gap-2">
                <ActionIcon className="w-4 h-4" />
                {action.label}
              </button>
            )
          )}
          {secondaryAction && (
            secondaryAction.href ? (
              <a href={secondaryAction.href} className="btn-secondary">
                {secondaryAction.label}
              </a>
            ) : (
              <button onClick={secondaryAction.onClick} className="btn-secondary">
                {secondaryAction.label}
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
}
